/**
 * Hero slider: stacked slides that cross-fade. Advances every `data-interval` seconds and on a click
 * anywhere on the slide (except its button), which also restarts the countdown. The timer only runs
 * while the slider is on screen and the tab is visible; in the theme editor it stays on the selected slide.
 */
class HeroSliderTtt extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLElement | null} */
  #current = null;
  #index = 0;
  /** @type {number | undefined} */
  #timer;
  #visible = false;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.#current = this.querySelector('[data-current]');
    if (this.#slides.length < 2) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('visibilitychange', this.#restart);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      this.#restart();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#restart);
    window.clearTimeout(this.#timer);
  }

  get #interval() {
    return Number(this.dataset.interval ?? 0) * 1000;
  }

  /** @param {number} index */
  #show(index) {
    const count = this.#slides.length;
    this.#index = ((index % count) + count) % count;

    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });

    if (this.#current) this.#current.textContent = String(this.#index + 1).padStart(2, '0');
  }

  #restart = () => {
    window.clearTimeout(this.#timer);
    if (!this.#visible || document.hidden || window.Shopify?.designMode || this.#interval <= 0) return;

    this.#timer = window.setTimeout(() => {
      this.#show(this.#index + 1);
      this.#restart();
    }, this.#interval);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.target instanceof Element && event.target.closest('a, button')) return;
    this.#show(this.#index + 1);
    this.#restart();
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const slide = event.target instanceof HTMLElement ? event.target.closest('[data-slide]') : null;
    if (slide instanceof HTMLElement) this.#show(this.#slides.indexOf(slide));
  };
}

if (!customElements.get('hero-slider-ttt')) {
  customElements.define('hero-slider-ttt', HeroSliderTtt);
}
